import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import './ImageModal.css';

/**
 * 이미지를 크게 볼 수 있는 모달 뷰어
 * 확대/축소, 다운로드 등의 기능을 제공합니다.
 */
const ImageModal = ({ isOpen, onClose, imageData }) => {
  const [zoom, setZoom] = useState(1);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: 0, y: 0 });
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  // 모달이 열릴 때마다 초기화 및 body 스크롤 제어
  useEffect(() => {
    if (isOpen) {
      setZoom(1);
      setPosition({ x: 0, y: 0 });
      setIsDragging(false);
      
      // body 스크롤 비활성화
      const originalOverflow = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      
      return () => {
        // 컴포넌트 언마운트 시 body 스크롤 복원
        document.body.style.overflow = originalOverflow;
      };
    }
  }, [isOpen]);

  // ESC 키로 모달 닫기
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && isOpen) {
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen || !imageData) return null;

  // 줌 조절 함수
  const handleZoomIn = () => {
    setZoom(prev => Math.min(prev + 0.25, 3));
  };

  const handleZoomOut = () => {
    setZoom(prev => Math.max(prev - 0.25, 0.25));
  };

  const handleResetZoom = () => {
    setZoom(1);
    setPosition({ x: 0, y: 0 });
  };

  // 이미지 드래그 시작
  const handleMouseDown = (e) => {
    if (zoom > 1) {
      setIsDragging(true);
      setDragStart({
        x: e.clientX - position.x,
        y: e.clientY - position.y
      });
    }
  };

  // 이미지 드래그 중
  const handleMouseMove = (e) => {
    if (isDragging && zoom > 1) {
      setPosition({
        x: e.clientX - dragStart.x,
        y: e.clientY - dragStart.y
      });
    }
  };

  // 이미지 드래그 종료
  const handleMouseUp = () => {
    setIsDragging(false);
  };

  // 휠로 줌 조절
  const handleWheel = (e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -0.1 : 0.1;
    setZoom(prev => Math.max(0.25, Math.min(3, prev + delta)));
  };

  // 이미지 다운로드
  const handleDownload = () => {
    const link = document.createElement('a');
    link.href = imageData.url;
    link.download = imageData.filename || 'image.png';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // 백드롭 클릭 시 모달 닫기
  const handleBackdropClick = (e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  };

  // Portal을 사용하여 document.body에 직접 렌더링
  return createPortal(
    <div className="image-modal-backdrop" onClick={handleBackdropClick}>
      <div className="image-modal-content">
        {/* 헤더 */}
        <div className="image-modal-header">
          <div className="image-modal-title">
            {imageData.filename || '이미지 뷰어'}
          </div>
          <div className="image-modal-controls">
            <button 
              className="image-modal-btn zoom-btn" 
              onClick={handleZoomOut}
              title="축소"
            >
              −
            </button>
            <span className="zoom-level">{Math.round(zoom * 100)}%</span>
            <button 
              className="image-modal-btn zoom-btn" 
              onClick={handleZoomIn}
              title="확대"
            >
              +
            </button>
            <button 
              className="image-modal-btn reset-btn" 
              onClick={handleResetZoom}
              title="원본 크기"
            >
              ⌂
            </button>
            <button 
              className="image-modal-btn download-btn" 
              onClick={handleDownload}
              title="다운로드"
            >
              ⬇
            </button>
            <button 
              className="image-modal-btn close-btn" 
              onClick={onClose}
              title="닫기"
            >
              ✕
            </button>
          </div>
        </div>

        {/* 이미지 컨테이너 */}
        <div className="image-modal-body">
          <div 
            className="image-container"
            onMouseDown={handleMouseDown}
            onMouseMove={handleMouseMove}
            onMouseUp={handleMouseUp}
            onMouseLeave={handleMouseUp}
            onWheel={handleWheel}
          >
            <img
              src={imageData.file ? URL.createObjectURL(imageData.file) : imageData.url}
              alt={imageData.filename || '이미지'}
              className="modal-image"
              style={{
                transform: `scale(${zoom}) translate(${position.x / zoom}px, ${position.y / zoom}px)`,
                cursor: zoom > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default'
              }}
              draggable={false}
              onError={(e) => {
                // 이미지 로드 실패 시 URL로 다시 시도
                if (imageData.file) {
                  e.target.src = imageData.url;
                }
              }}
            />
          </div>
        </div>

        {/* 푸터 정보 */}
        <div className="image-modal-footer">
          <div className="image-info">
            {imageData.size && (
              <span className="file-size">
                크기: {(imageData.size / 1024).toFixed(1)} KB
              </span>
            )}
            {imageData.contentType && (
              <span className="file-type">
                형식: {imageData.contentType}
              </span>
            )}
          </div>
          <div className="modal-tips">
            마우스 휠로 확대/축소, 드래그로 이동 가능
          </div>
        </div>
      </div>
    </div>,
    document.body
  );
};

export default ImageModal; 